import { PDF_EXPORT_ROOT_ID } from "./pdfDimensions";

const REPORT_FONT = "Inter, system-ui, sans-serif";

async function loadReportFontFace(weight: number, sizePx: number) {
  if (!document.fonts?.load) {
    return;
  }
  try {
    await document.fonts.load(`${weight} ${sizePx}px ${REPORT_FONT}`);
  } catch {
    // Font loading API may reject; fall through to fonts.ready
  }
}

export async function waitForImages(root: ParentNode) {
  const images = Array.from(root.querySelectorAll("img"));
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }),
    ),
  );
}

export async function waitForPdfLayout(root: HTMLElement, timeoutMs = 15000) {
  if (root.dataset.pdfLayoutReady === "true") {
    return;
  }

  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    if (root.dataset.pdfLayoutReady === "true") {
      return;
    }
    await new Promise<void>((resolve) => setTimeout(resolve, 50));
  }

  throw new Error("PDF layout did not finish (section pagination timed out)");
}

export async function waitForFonts() {
  await Promise.all([
    loadReportFontFace(400, 16),
    loadReportFontFace(500, 12),
    loadReportFontFace(500, 24),
    loadReportFontFace(600, 32),
    loadReportFontFace(700, 48),
  ]);

  if (document.fonts?.ready) {
    await document.fonts.ready;
  }

  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

export async function waitForReportPdfReady(rootSelector = `#${PDF_EXPORT_ROOT_ID}`) {
  const root = document.querySelector<HTMLElement>(rootSelector);
  if (!root) {
    throw new Error(`PDF export root not found: ${rootSelector}`);
  }

  await waitForPdfLayout(root);
  await waitForImages(root);
  await waitForFonts();
}

export function markReportPdfReady() {
  document.documentElement.setAttribute("data-pdf-ready", "true");
  delete document.documentElement.dataset.pdfError;
}

export function markReportPdfError(message: string) {
  document.documentElement.setAttribute("data-pdf-error", message);
  document.documentElement.removeAttribute("data-pdf-ready");
}

export function downloadPdfBlob(blob: Blob, filename = "AI_Readiness_Report.pdf") {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
